import {applyNodeChanges, applyEdgeChanges} from '@xyflow/react';
import {
  removeWithCascade,
  partitionNodeChanges,
  groupNodes,
  ungroupNode,
} from '../util/graphModel';
import {traverseNodes, traverseElements} from '../util/graphTraversal';
import {
  LOAD_MAP,
  SET_MAP_PROPERTY,
  APPLY_NODE_CHANGES,
  APPLY_EDGE_CHANGES,
  ADD_NODE,
  UPDATE_NODE,
  ADD_EDGE,
  UPDATE_EDGE,
  REMOVE,
  ADD_ELEMENT,
  UPDATE_ELEMENT,
  ADD_TABLE,
  UPDATE_TABLE,
  UPDATE_TABLE_POS,
  UPDATE_GROUP_POS,
  UPDATE_DATA_NODE_POS,
  ADD_MAPPING,
  UPDATE_MAPPING,
  GROUP_NODES,
  UNGROUP_NODE,
  TOGGLE_COLLAPSE,
  SET_SELECTION,
  CLEAR_SELECTION,
  SET_LINEAGE_VIEW_TYPE,
  SET_HIGHLIGHT_MODE,
  OPEN_DRAWER,
  CLOSE_DRAWER,
  SET_VIEW_MODE,
  COMMIT_HISTORY,
  UNDO,
  REDO,
  MARK_DIRTY,
  SAVE_REQUEST,
  SAVE_SUCCESS,
  SAVE_FAILURE,
  PUBLISH_REQUEST,
  PUBLISH_SUCCESS,
  PUBLISH_FAILURE,
  CHAR_LOAD_SUCCESS,
  CHAR_LOAD_FAILURE,
  CHAR_CREATE_SUCCESS,
  CHAR_UPDATE_SUCCESS,
  CHAR_DELETE_SUCCESS,
  NODEREF_LOAD_SUCCESS,
  NODEREF_CREATE_SUCCESS,
  NODEREF_UPDATE_SUCCESS,
  NODEREF_DELETE_SUCCESS,
  DD_SCHEMAS_SUCCESS,
  DD_OBJECTS_SUCCESS,
  DD_COLUMNS_SUCCESS,
  DD_CONNECTIONS_SUCCESS,
  MERGE_VIEW_LINEAGE,
  IMPORT_LINEAGE,
  NOTIFY,
  DISMISS_NOTIFY,
  GLOBAL_SETTINGS_LOAD_SUCCESS,
  GLOBAL_SETTINGS_SAVE_SUCCESS,
  SET_GLOBAL_THEME,
  SET_DISPLAY_MODE,
  CLEAR_DATA_VIEW_LAYOUT,
} from './lineageActions';

const emptyMap = () => ({
  id: '',
  name: '',
  description: '',
  status: 'draft',
  lockVersion: null,
  publishedVersion: null,
  nodes: [],
  edges: [],
  mappings: [],
  settings: {},
});

const emptyHighlight = {
  nodeIds: [],
  downstreamEdgeIds: [],
  upstreamEdgeIds: [],
  elementKeys: [],
  mappingIds: [],
};

const initialState = {
  map: emptyMap(),
  viewMode: 'edit',
  lineageViewType: 'system',
  displayMode: 'graph',
  highlightMode: 'full',
  selection: {nodeId: null, elementId: null},
  highlight: emptyHighlight,
  drawerOpen: false,
  drawerMode: 'edit',
  activeNodeId: null,
  drawerElementId: null,
  activeTableId: null,
  history: [],
  historyIndex: -1,
  // Persistence
  saveState: 'idle', // 'idle' | 'saving' | 'saved' | 'error'
  lastSavedAt: null,
  dirty: false,
  saveError: null,
  // Characteristics global config
  characteristics: [],
  // Slice 6c-3b: non-null when the characteristic load was REFUSED or failed, so the editor can say
  // so instead of rendering an empty palette that looks like a configured empty state.
  charLoadError: null,
  // Node reference catalog (non-database node types)
  nodeRefs: [],
  // DD picker caches
  ddSchemas: [],
  ddObjects: {}, // { [schemaId]: [{objectId, objectName, objectType}] }
  ddColumns: {}, // { [objectId]: [{columnId, columnName, dataType}] }
  connections: [],
  // Transient notification (toast)
  notification: null, // {message, actionLabel, actionFn} | null
  // Global canvas theme (from /lineage/settings; overrides per-diagram settings.theme)
  globalTheme: null,
  globalThemeLoading: false,
};

const editElements = (node, tableId, fn) => {
  if (tableId) {
    return {...node, data: {...node.data, tables: (node.data.tables || []).map((t) =>
      t.id === tableId ? {...t, dataElements: fn(t.dataElements || [])} : t)}};
  }
  return {...node, data: {...node.data, dataElements: fn(node.data.dataElements || [])}};
};

const snapshot = (map) => ({nodes: map.nodes, edges: map.edges, mappings: map.mappings});

const pushHistory = (state, nextMap) => {
  const item = snapshot(nextMap);
  const history = [...state.history.slice(0, state.historyIndex + 1), item].slice(-50);
  return {history, historyIndex: Math.min(history.length - 1, 49)};
};

const computeHighlight = (map, selection, mode, lineageViewType) => {
  if (!selection.nodeId) return emptyHighlight;
  const depth = mode === 'immediate' ? 1 : null;
  if (lineageViewType === 'data' && selection.elementId != null) {
    const {elementKeys, mappingIds, nodeIds} =
      traverseElements(map.nodes, map.edges, map.mappings, selection.nodeId, selection.elementId,
          {depth, selectedTableId: selection.tableId});
    return {
      nodeIds: [...nodeIds],
      downstreamEdgeIds: [],
      upstreamEdgeIds: [],
      elementKeys: [...elementKeys],
      mappingIds: [...mappingIds],
    };
  }
  const {nodeIds, downstreamEdgeIds, upstreamEdgeIds} =
    traverseNodes(map.nodes, map.edges, selection.nodeId, {depth});
  return {
    nodeIds: [...nodeIds],
    downstreamEdgeIds: [...downstreamEdgeIds],
    upstreamEdgeIds: [...upstreamEdgeIds],
    elementKeys: [],
    mappingIds: [],
  };
};

const withMap = (state, nextMap, {commit = true, recompute = true} = {}) => {
  const highlight = recompute ?
    computeHighlight(nextMap, state.selection, state.highlightMode, state.lineageViewType) :
    state.highlight;
  return {
    ...state,
    map: nextMap,
    highlight,
    dirty: commit ? true : state.dirty,
    ...(commit ? pushHistory(state, nextMap) : {}),
  };
};

const lineageReducer = (state = initialState, action) => {
  const {map} = state;
  switch (action.type) {
    case LOAD_MAP: {
      const p = action.payload || {};
      const nextMap = {
        ...emptyMap(),
        ...p,
        nodes: p.nodes || [],
        edges: p.edges || [],
        mappings: p.mappings || [],
        settings: p.settings || {},
        lockVersion: p.lockVersion != null ? p.lockVersion : null,
        publishedVersion: p.publishedVersion != null ? p.publishedVersion : null,
      };
      return {
        ...initialState,
        // Preserve non-map slices that may already be populated (characteristics, caches)
        characteristics: state.characteristics,
        nodeRefs: state.nodeRefs,
        ddSchemas: state.ddSchemas,
        ddObjects: state.ddObjects,
        ddColumns: state.ddColumns,
        connections: state.connections,
        globalTheme: state.globalTheme,
        globalThemeLoading: state.globalThemeLoading,
        displayMode: state.displayMode,
        map: nextMap,
        history: [snapshot(nextMap)],
        historyIndex: 0,
        dirty: false,
        saveState: 'idle',
      };
    }

    case SET_MAP_PROPERTY:
      // Rename / describe the diagram: mark dirty so autosave persists it (name isn't
      // part of the node/edge snapshot, so no history push needed).
      return {...state, map: {...map, [action.payload.key]: action.payload.value}, dirty: true};

    case MARK_DIRTY:
      return {...state, dirty: true};

    // ---- Persistence ----
    case SAVE_REQUEST:
      return {...state, saveState: 'saving', saveError: null};

    case SAVE_SUCCESS:
      return {
        ...state,
        saveState: 'saved',
        lastSavedAt: new Date().toISOString(),
        dirty: false,
        saveError: null,
        map: {...map, lockVersion: action.payload.lockVersion != null ? action.payload.lockVersion : map.lockVersion},
      };

    case SAVE_FAILURE:
      return {...state, saveState: 'error', saveError: action.payload};

    case PUBLISH_REQUEST:
      return {...state, saveState: 'saving'};

    case PUBLISH_SUCCESS:
      return {
        ...state,
        saveState: 'saved',
        lastSavedAt: new Date().toISOString(),
        dirty: false,
        viewMode: 'view',
        map: {
          ...map,
          status: 'published',
          publishedVersion: action.payload.publishedVersion != null ? action.payload.publishedVersion : map.publishedVersion,
          lockVersion: action.payload.lockVersion != null ? action.payload.lockVersion : map.lockVersion,
        },
      };

    case PUBLISH_FAILURE:
      return {...state, saveState: 'error', saveError: action.payload};

    // ---- Characteristics ----
    case CHAR_LOAD_SUCCESS:
      return {...state, characteristics: action.payload, charLoadError: null};

    case CHAR_LOAD_FAILURE:
      return {...state, characteristics: [], charLoadError: action.payload};

    case CHAR_CREATE_SUCCESS:
      return {...state, characteristics: [...state.characteristics, action.payload]};

    case CHAR_UPDATE_SUCCESS:
      return {
        ...state,
        characteristics: state.characteristics.map((c) =>
          c.id === action.payload.id ? {...c, ...action.payload} : c),
      };

    case CHAR_DELETE_SUCCESS:
      return {
        ...state,
        characteristics: state.characteristics.filter((c) => c.id !== action.payload),
      };

    // ---- Node Refs ----
    case NODEREF_LOAD_SUCCESS:
      return {...state, nodeRefs: action.payload};

    case NODEREF_CREATE_SUCCESS:
      return {...state, nodeRefs: [...state.nodeRefs, action.payload]};

    case NODEREF_UPDATE_SUCCESS:
      return {
        ...state,
        nodeRefs: state.nodeRefs.map((r) =>
          r.id === action.payload.id ? {...r, ...action.payload} : r),
      };

    case NODEREF_DELETE_SUCCESS:
      return {
        ...state,
        nodeRefs: state.nodeRefs.map((r) =>
          r.id === action.payload ? {...r, isActive: 0} : r),
      };

    // ---- DD caches ----
    case DD_SCHEMAS_SUCCESS:
      return {...state, ddSchemas: action.payload};

    case DD_OBJECTS_SUCCESS:
      return {...state, ddObjects: {...state.ddObjects, [action.payload.schemaId]: action.payload.objects}};

    case DD_COLUMNS_SUCCESS:
      return {...state, ddColumns: {...state.ddColumns, [action.payload.objectId]: action.payload.columns}};

    case DD_CONNECTIONS_SUCCESS:
      return {...state, connections: action.payload};

    // ---- React Flow change streams ----
    case APPLY_NODE_CHANGES: {
      const {removeIds, passthrough} = partitionNodeChanges(action.payload.changes, map.nodes);
      let nextMap = map;
      removeIds.forEach((id) => {
        nextMap = {...nextMap, ...removeWithCascade(nextMap, {node: id})};
      });
      const nextNodes = applyNodeChanges(passthrough, nextMap.nodes);
      nextMap = {...nextMap, nodes: nextNodes};
      return withMap(state, nextMap, {commit: removeIds.length > 0, recompute: removeIds.length > 0});
    }

    case APPLY_EDGE_CHANGES: {
      const removeIds = action.payload.changes.filter((c) => c.type === 'remove').map((c) => c.id);
      const passthrough = action.payload.changes.filter((c) => c.type !== 'remove');
      let nextMap = map;
      removeIds.forEach((id) => {
        nextMap = {...nextMap, ...removeWithCascade(nextMap, {edge: id})};
      });
      nextMap = {...nextMap, edges: applyEdgeChanges(passthrough, nextMap.edges)};
      return withMap(state, nextMap, {commit: removeIds.length > 0, recompute: removeIds.length > 0});
    }

    case ADD_NODE:
      return withMap(state, {...map, nodes: [...map.nodes, action.payload]});

    case UPDATE_NODE: {
      const {nodeId, updates} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id === nodeId ?
        {...n, ...updates, data: updates.data ? {...n.data, ...updates.data} : n.data} : n);
      const positionOnly = updates.position && Object.keys(updates).length === 1;
      return withMap(state, {...map, nodes: nextNodes}, {commit: !positionOnly, recompute: !positionOnly});
    }

    case ADD_EDGE:
      return withMap(state, {...map, edges: [...map.edges, action.payload]});

    case UPDATE_EDGE: {
      const {edgeId, updates} = action.payload;
      const nextEdges = map.edges.map((e) => e.id === edgeId ? {...e, ...updates} : e);
      return withMap(state, {...map, edges: nextEdges});
    }

    case REMOVE:
      return withMap(state, {...map, ...removeWithCascade(map, action.payload.target)});

    case ADD_ELEMENT: {
      const {nodeId, tableId, element} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id !== nodeId ? n :
        editElements(n, tableId, (els) => [...els, element]));
      return withMap(state, {...map, nodes: nextNodes});
    }

    case UPDATE_ELEMENT: {
      const {nodeId, tableId, elementId, updates} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id !== nodeId ? n :
        editElements(n, tableId, (els) => els.map((el) => el.id === elementId ? {...el, ...updates} : el)));
      return withMap(state, {...map, nodes: nextNodes});
    }

    case ADD_TABLE: {
      const {nodeId, table} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id === nodeId ?
        {...n, data: {...n.data, tables: [...(n.data.tables || []), table]}} : n);
      return withMap(state, {...map, nodes: nextNodes});
    }

    case UPDATE_TABLE: {
      const {nodeId, tableId, updates} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id === nodeId ? {
        ...n,
        data: {...n.data, tables: (n.data.tables || []).map((t) => t.id === tableId ? {...t, ...updates} : t)},
      } : n);
      return withMap(state, {...map, nodes: nextNodes});
    }

    case UPDATE_TABLE_POS: {
      // Store a data-view card position on node.data.details.dvMeta[tableId].dataPos.
      // Non-committing per-frame (no history/highlight churn); the drag-stop COMMIT_HISTORY
      // snapshots it + marks dirty. dvMeta rides details_json, so positions persist.
      const {nodeId, tableId, pos} = action.payload;
      const nextNodes = map.nodes.map((n) => {
        if (n.id !== nodeId) return n;
        const details = n.data.details || {};
        const dvMeta = details.dvMeta || {};
        const entry = dvMeta[tableId] || {};
        return {...n, data: {...n.data, details: {...details,
          dvMeta: {...dvMeta, [tableId]: {...entry, dataPos: pos}}}}};
      });
      return withMap(state, {...map, nodes: nextNodes}, {commit: false, recompute: false});
    }

    case UPDATE_GROUP_POS: {
      // Data-view position of a whole derived group (cluster). Stored on node.data.details.dvGroupPos
      // — display-only (never touches the system-view node.position). Non-committing per-frame; the
      // drag-stop COMMIT_HISTORY snapshots + marks dirty. Rides details_json, so it persists.
      const {nodeId, pos} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id !== nodeId ? n :
        {...n, data: {...n.data, details: {...(n.data.details || {}), dvGroupPos: pos}}});
      return withMap(state, {...map, nodes: nextNodes}, {commit: false, recompute: false});
    }

    case UPDATE_DATA_NODE_POS: {
      // Data-view position of a top-level plain app node. Stored on node.data.details.dvNodePos
      // — display-only (never touches the system-view node.position). Non-committing per-frame; the
      // drag-stop COMMIT_HISTORY snapshots + marks dirty. Rides details_json, so it persists.
      const {nodeId, pos} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id !== nodeId ? n :
        {...n, data: {...n.data, details: {...(n.data.details || {}), dvNodePos: pos}}});
      return withMap(state, {...map, nodes: nextNodes}, {commit: false, recompute: false});
    }

    case CLEAR_DATA_VIEW_LAYOUT: {
      const dropKey = (obj, key) => Object.fromEntries(Object.entries(obj).filter(([k]) => k !== key));
      const nextNodes = map.nodes.map((n) => {
        const details = n.data && n.data.details;
        if (!details) return n;
        const hasDvGroupPos = !!details.dvGroupPos;
        const hasDvNodePos = !!details.dvNodePos;
        const dvMeta = details.dvMeta;
        const hasDvMeta = dvMeta && Object.keys(dvMeta).some((k) => dvMeta[k] && dvMeta[k].dataPos != null);
        if (!hasDvGroupPos && !hasDvNodePos && !hasDvMeta) return n;
        const nextDvMeta = dvMeta ? Object.fromEntries(
            Object.entries(dvMeta).map(([k, v]) => [k, (v && v.dataPos != null) ? dropKey(v, 'dataPos') : v]),
        ) : dvMeta;
        return {...n, data: {...n.data, details: {
          ...dropKey(dropKey(details, 'dvGroupPos'), 'dvNodePos'), dvMeta: nextDvMeta,
        }}};
      });
      return withMap(state, {...map, nodes: nextNodes}, {commit: true, recompute: false});
    }

    case ADD_MAPPING:
      return withMap(state, {...map, mappings: [...map.mappings, action.payload]});

    case UPDATE_MAPPING: {
      const {mappingId, updates} = action.payload;
      const nextMappings = map.mappings.map((m) => m.id === mappingId ? {...m, ...updates} : m);
      return withMap(state, {...map, mappings: nextMappings});
    }

    case GROUP_NODES:
      return withMap(state, groupNodes(map, action.payload.memberIds, action.payload.groupId));

    case UNGROUP_NODE:
      return withMap(state, ungroupNode(map, action.payload.groupId));

    case TOGGLE_COLLAPSE: {
      const {groupId} = action.payload;
      const nextNodes = map.nodes.map((n) => n.id === groupId ?
        {...n, data: {...n.data, collapsed: !(n.data && n.data.collapsed)}} : n);
      return withMap(state, {...map, nodes: nextNodes});
    }

    case SET_SELECTION: {
      const selection = {
        nodeId: action.payload.nodeId,
        elementId: action.payload.elementId ?? null,
        tableId: action.payload.tableId ?? null,
      };
      return {
        ...state,
        selection,
        highlight: computeHighlight(map, selection, state.highlightMode, state.lineageViewType),
      };
    }

    case CLEAR_SELECTION:
      return {...state, selection: {nodeId: null, elementId: null}, highlight: emptyHighlight};

    case SET_HIGHLIGHT_MODE: {
      const highlightMode = action.payload;
      return {
        ...state,
        highlightMode,
        highlight: computeHighlight(map, state.selection, highlightMode, state.lineageViewType),
      };
    }

    case SET_LINEAGE_VIEW_TYPE: {
      const lineageViewType = action.payload;
      return {
        ...state,
        lineageViewType,
        selection: {nodeId: null, elementId: null},
        highlight: emptyHighlight,
      };
    }

    case GLOBAL_SETTINGS_LOAD_SUCCESS:
      return {...state, globalTheme: action.payload.theme, globalThemeLoading: false};

    case GLOBAL_SETTINGS_SAVE_SUCCESS:
      return {...state, globalTheme: action.payload.theme};

    case SET_GLOBAL_THEME:
      return {...state, globalTheme: action.payload};

    case SET_DISPLAY_MODE:
      return {...state, displayMode: action.payload};

    case SET_VIEW_MODE:
      return {...state, viewMode: action.payload, selection: {nodeId: null, elementId: null}, highlight: emptyHighlight};

    case OPEN_DRAWER:
      return {
        ...state,
        drawerOpen: true,
        drawerMode: action.payload.mode,
        activeNodeId: action.payload.nodeId,
        drawerElementId: action.payload.elementId ?? null,
        activeTableId: action.payload.tableId ?? null,
      };

    case CLOSE_DRAWER:
      return {...state, drawerOpen: false, activeNodeId: null, drawerElementId: null, activeTableId: null};

    case COMMIT_HISTORY:
      // Mark dirty so a committed change (incl. node/card drag-stops and Tidy) triggers
      // autosave — position moves go through applyNodeChanges with commit:false, so the
      // drag-stop COMMIT_HISTORY is the only signal that a move needs persisting.
      return {...state, ...pushHistory(state, map), dirty: true};

    case UNDO: {
      if (state.historyIndex <= 0) return state;
      const prev = state.history[state.historyIndex - 1];
      return {
        ...state, map: {...map, ...prev}, historyIndex: state.historyIndex - 1,
        highlight: emptyHighlight, selection: {nodeId: null, elementId: null},
      };
    }

    case REDO: {
      if (state.historyIndex >= state.history.length - 1) return state;
      const next = state.history[state.historyIndex + 1];
      return {
        ...state, map: {...map, ...next}, historyIndex: state.historyIndex + 1,
        highlight: emptyHighlight, selection: {nodeId: null, elementId: null},
      };
    }

    case MERGE_VIEW_LINEAGE: {
      const {dbNodeId, viewTableId, tablesToAdd, mappingsToAdd, viewElementsToMerge} = action.payload;
      // Guard: db node or view table must still exist at reduce time
      const dbNode = map.nodes.find((n) => n.id === dbNodeId);
      if (!dbNode) return state;
      const viewTable = (dbNode.data.tables || []).find((t) => t.id === viewTableId);
      if (!viewTable) return state;

      // Merge view elements into the view table by columnId (preserve existing / isCde)
      const existingViewCols = new Set((viewTable.dataElements || []).map((e) => e.columnId).filter(Boolean));
      const newViewElements = viewElementsToMerge.filter((e) => !existingViewCols.has(e.columnId));

      // Dedupe incoming source slots against slots already on the node (by truthy objectId)
      const existingSlotObjIds = new Set((dbNode.data.tables || []).map((t) => t.objectId).filter(Boolean));
      const dedupedTables = tablesToAdd.filter((t) => !(t.objectId && existingSlotObjIds.has(t.objectId)));

      // dvMeta: mark the view slot 'view' and every mapping-source slot 'source'
      // (covers reused slots, not just freshly-added ones). Preserve existing dataPos.
      const details = dbNode.data.details || {};
      const dvMeta = {...(details.dvMeta || {})};
      const mark = (id, role) => {
        dvMeta[id] = {...(dvMeta[id] || {}), role};
      };
      mark(viewTableId, 'view');
      mappingsToAdd.forEach((m) => m.sourceTableId && mark(m.sourceTableId, 'source'));

      const nextNodes = map.nodes.map((n) => {
        if (n.id !== dbNodeId) return n;
        const mergedTables = (n.data.tables || []).map((t) =>
          t.id === viewTableId ? {...t, dataElements: [...(t.dataElements || []), ...newViewElements]} : t);
        return {
          ...n,
          data: {...n.data, details: {...details, dvMeta}, tables: [...mergedTables, ...dedupedTables]},
        };
      });

      // Intra-node mappings use edgeId `self-${dbNodeId}` (a sentinel) — no map.edges added.
      const nextMap = {...map, nodes: nextNodes, mappings: [...map.mappings, ...mappingsToAdd]};
      return withMap(state, nextMap, {commit: true, recompute: true});
    }

    case IMPORT_LINEAGE: {
      const {nodesToAdd = [], nodeUpserts = [], edgesToAdd = [], edgeUpdates = [], mappingsToAdd = [], mappingUpdates = []} = action.payload;

      const dedupeById = (arr) => {
        const seen = new Set();
        return arr.filter((x) => seen.has(x.id) ? false : seen.add(x.id));
      };

      let newNodes = dedupeById([...map.nodes, ...nodesToAdd]);

      newNodes = newNodes.map((node) => {
        const upsert = nodeUpserts.find((u) => u.nodeId === node.id);
        if (!upsert) return node;

        const existingTableIds = new Set((node.data.tables || []).map((t) => t.id));
        const newTables = (upsert.tablesToAdd || []).filter((t) => !existingTableIds.has(t.id));

        const mergedTables = [
          ...(node.data.tables || []).map((t) => {
            const incomingTbl = (upsert.tablesToAdd || []).find((it) => it.id === t.id);
            if (!incomingTbl) return t;
            const existingElIds = new Set((t.dataElements || []).map((e) => e.id));
            const newEls = (incomingTbl.dataElements || []).filter((e) => !existingElIds.has(e.id));
            return newEls.length ? {...t, dataElements: [...(t.dataElements || []), ...newEls]} : t;
          }),
          ...newTables,
        ];

        const existingElIds = new Set((node.data.dataElements || []).map((e) => e.id));
        const newEls = (upsert.elementsToAdd || []).filter((e) => !existingElIds.has(e.id));

        return {...node, data: {...node.data, tables: mergedTables,
          dataElements: newEls.length ? [...(node.data.dataElements || []), ...newEls] : node.data.dataElements || []}};
      });

      let newEdges = dedupeById([...map.edges, ...edgesToAdd]);
      newEdges = newEdges.map((e) => {
        const upd = edgeUpdates.find((u) => u.id === e.id);
        return upd ? {...e, direction: upd.direction} : e;
      });

      let newMappings = dedupeById([...map.mappings, ...mappingsToAdd]);
      newMappings = newMappings.map((m) => {
        const upd = mappingUpdates.find((u) => u.id === m.id);
        return upd ? {...m, direction: upd.direction} : m;
      });

      return withMap(state, {...map, nodes: newNodes, edges: newEdges, mappings: newMappings}, {commit: true, recompute: true});
    }

    case NOTIFY:
      return {...state, notification: action.payload};

    case DISMISS_NOTIFY:
      return {...state, notification: null};

    default:
      return state;
  }
};

export default lineageReducer;
